import { useCallback, useState } from 'react'
import { toast } from 'sonner'
import { useImageUpload } from '../../common/hooks/use-file-upload'
import { patchBrandKit } from '../api/brand-kit-api'
import { useBrandKitStore } from '../store/brand-kit-store'

/**
 * Brand logo upload/remove, shared by the Brand Kit page and the onboarding
 * conversation panel so the optimistic-blob → publicUrl → persist-attachment-id
 * flow lives in exactly one place. Updates `brandKit.logoId` in the store
 * (optimistically with a blob URL, then the real publicUrl) and persists the
 * attachment id — never the signed URL — to the server.
 */
export function useLogoUpload() {
  const updateBrandKit = useBrandKitStore(s => s.updateBrandKit)
  const { handleUpload } = useImageUpload({ maxSize: 10 * 1024 * 1024 })
  const [uploading, setUploading] = useState(false)

  const uploadLogo = useCallback(
    async (file: File) => {
      const blobUrl = URL.createObjectURL(file)
      updateBrandKit({ logoId: blobUrl })
      setUploading(true)
      try {
        const attachment = await handleUpload(file)
        updateBrandKit({ logoId: attachment.publicUrl })
        URL.revokeObjectURL(blobUrl)
        await patchBrandKit({ logoId: attachment.id })
      } catch (err) {
        const msg = err instanceof Error ? err.message : ''
        // Validation rejections are EXPECTED user input (someone picked a .pdf
        // or a 20MB photo) — toast them instead of the old console-only throw
        // where nothing visibly happened, and keep them out of the
        // console bridge. Real upload failures still log + toast.
        if (msg === 'Invalid file type') {
          toast.error("That file won't work as a logo. Use a PNG or JPG image.")
        } else if (msg.startsWith('File size should be less than')) {
          toast.error('That image is too big. Keep it under 10MB.')
        } else {
          console.error('useLogoUpload: logo upload failed', err)
          toast.error("Couldn't upload the logo. Please try again.")
        }
        updateBrandKit({ logoId: null })
        URL.revokeObjectURL(blobUrl)
      } finally {
        setUploading(false)
      }
    },
    [handleUpload, updateBrandKit]
  )

  const removeLogo = useCallback(async () => {
    updateBrandKit({ logoId: null })
    try {
      await patchBrandKit({ logoId: null })
    } catch (err) {
      console.error('useLogoUpload: logo remove failed', err)
      toast.error("Couldn't remove the logo. Please try again.")
    }
  }, [updateBrandKit])

  return { uploading, uploadLogo, removeLogo }
}
